// FieldIQ — Topics tab + topic detail view

import { useState, useEffect } from "react";
import {
  MessageSquare,
  Loader2,
  ChevronRight,
  ChevronLeft,
  Search,
  FileText,
  BookOpen,
  TrendingUp,
  WifiOff,
} from "lucide-react";
import { Avatar, Badge } from "../components/ui";
import { authFetch } from "../lib/api";

export const TopicDetailView = ({ topic, onBack, onAskLidy }) => {
  // Normalise camelCase (hardcoded constant) and snake_case (API response)
  const heroStat = topic.hero_stat || topic.heroStat || {};
  const talkingPoints = topic.talking_points || topic.talkingPoints || [];
  const candidates = topic.candidates || [];
  const facts = topic.facts || [];
  const sources = topic.sources || [];

  return (
    <div className="space-y-5">
      <button
        onClick={onBack}
        className="flex items-center gap-1.5 text-sm text-cyan-400 hover:text-cyan-300 transition-colors"
      >
        <ChevronLeft size={16} />
        Topics
      </button>

      <div>
        <div className="flex items-center gap-2 flex-wrap">
          <h1 className="text-xl font-bold text-white">{topic.label}</h1>
          {topic.hot && <Badge label="HOT" color="amber" />}
        </div>
        <p className="text-sm text-slate-400 mt-1">{topic.summary}</p>
      </div>

      {/* Hero stat */}
      {heroStat.value && (
        <div className="bg-gradient-to-br from-cyan-900/40 to-blue-900/40 border border-cyan-700/30 rounded-2xl p-5">
          <div className="text-4xl font-bold text-white">{heroStat.value}</div>
          <div className="text-sm font-medium text-cyan-300 mt-1">
            {heroStat.label}
          </div>
          {heroStat.trend && (
            <div className="flex items-center gap-1.5 mt-2 text-xs text-green-400">
              <TrendingUp size={12} />
              {heroStat.trend}
            </div>
          )}
        </div>
      )}

      {/* Talking Points */}
      {talkingPoints.length > 0 && (
        <div>
          <h2 className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-3">
            Talking Points
          </h2>
          <div className="space-y-2">
            {talkingPoints.map((pt, i) => (
              <div
                key={i}
                className="flex gap-3 bg-slate-800 border border-slate-700 rounded-xl px-4 py-3"
              >
                <span className="text-cyan-400 font-bold text-sm flex-shrink-0 w-5">
                  {i + 1}.
                </span>
                <p className="text-sm text-white leading-relaxed">{pt}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Candidate Positions */}
      {candidates.length > 0 && (
        <div>
          <h2 className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-3">
            Candidate Positions
          </h2>
          <div className="space-y-2">
            {candidates.map((c, i) => (
              <div
                key={i}
                className="bg-slate-800 border border-slate-700 rounded-xl px-4 py-3 flex gap-3"
              >
                <Avatar name={c.name} size={8} />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-white">{c.name}</p>
                  <p className="text-xs text-slate-400 mt-0.5 leading-relaxed">
                    {c.position}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Key Facts */}
      {facts.length > 0 && (
        <div>
          <h2 className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-3">
            Key Facts
          </h2>
          <div className="grid grid-cols-2 gap-2">
            {facts.map((f, i) => (
              <div
                key={i}
                className="bg-slate-800 border border-slate-700 rounded-xl p-3"
              >
                <div className="text-lg font-bold text-cyan-400">{f.value}</div>
                <div className="text-xs text-slate-400 mt-0.5">{f.label}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Sources */}
      {sources.length > 0 && (
        <div>
          <h2 className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-3">
            Sources
          </h2>
          <div className="space-y-1.5">
            {sources.map((s, i) => (
              <div
                key={i}
                className="flex items-center gap-2 bg-slate-800 border border-slate-700 rounded-xl px-4 py-2.5"
              >
                <FileText size={14} className="text-slate-500 flex-shrink-0" />
                <span className="text-sm text-slate-300">{s.name}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Ask Lidy CTA */}
      <button
        onClick={() => onAskLidy?.(topic.label)}
        className="w-full flex items-center justify-center gap-2 py-3.5 bg-gradient-to-r from-cyan-600 to-blue-700 text-white font-medium rounded-xl hover:from-cyan-500 hover:to-blue-600 transition-colors"
      >
        <MessageSquare size={16} />
        Ask Lidy about {topic.label}
      </button>

      <div className="h-2" />
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════
// TOPICS TAB
// ═══════════════════════════════════════════════════════════════════

export const TopicsTab = ({ facility, onOpenTopic, isOnline }) => {
  const [search, setSearch] = useState("");
  const [topics, setTopics] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fid = facility?.facility_id || "";
    const qs = fid ? `?facility_id=${encodeURIComponent(fid)}` : "";
    authFetch(`/fieldiq/topics${qs}`)
      .then((data) => {
        if (Array.isArray(data) && data.length > 0) setTopics(data);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [facility]);

  const filtered = topics.filter(
    (t) =>
      t.label.toLowerCase().includes(search.toLowerCase()) ||
      (t.summary || "").toLowerCase().includes(search.toLowerCase()),
  );

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-lg font-semibold text-white">Topics</h1>
        <p className="text-xs text-slate-500 mt-0.5">
          Issue briefings &amp; talking points
        </p>
      </div>

      {!isOnline && (
        <div className="flex items-center gap-2 px-3 py-2 bg-amber-900/40 border border-amber-700/50 rounded-xl text-amber-300 text-xs">
          <WifiOff size={13} />
          Offline — topic briefings require a connection.
        </div>
      )}

      <div className="relative">
        <Search
          size={14}
          className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500"
        />
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search topics..."
          className="w-full pl-8 pr-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-sm text-white placeholder-slate-500 focus:border-cyan-500 focus:outline-none"
        />
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-10">
          <Loader2 size={20} className="text-cyan-400 animate-spin" />
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map((topic) => (
            <button
              key={topic.id || topic.slug}
              onClick={() => onOpenTopic(topic)}
              className="w-full flex items-center gap-3 bg-slate-800 border border-slate-700 rounded-xl px-4 py-3 hover:border-slate-600 transition-colors text-left"
            >
              <BookOpen size={16} className="text-cyan-400 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-white">
                    {topic.label}
                  </span>
                  {topic.hot && <Badge label="HOT" color="amber" />}
                </div>
                <p className="text-xs text-slate-500 mt-0.5 truncate">
                  {topic.summary}
                </p>
              </div>
              <ChevronRight
                size={16}
                className="text-slate-600 flex-shrink-0"
              />
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════
// PEOPLE TAB — Voters (roster) + Team with segmented control
